import { create } from 'zustand'
import type { User } from '@/api/auth'
import { authApi } from '@/api/auth'

const REFRESH_TOKEN_KEY = 'copilot-auth-refresh'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  restoreSession: () => Promise<boolean>
  refreshAccessToken: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true, // Start true to check for existing session

  setAuth: (user, accessToken, refreshToken) => {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    set({
      user,
      token: accessToken,
      isAuthenticated: true,
      isLoading: false,
    })
  },

  logout: () => {
    const { token } = get()
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

    // Best-effort server logout
    if (token && refreshToken) {
      authApi.logoutServer(token, refreshToken)
    }

    localStorage.removeItem(REFRESH_TOKEN_KEY)
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    })
  },

  setLoading: (loading) =>
    set({ isLoading: loading }),

  restoreSession: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      set({ isLoading: false })
      return false
    }

    try {
      const response = await authApi.refreshToken(refreshToken)
      // Save rotated refresh token
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)

      // Fetch user profile with the new access token
      const userResponse = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/v1/auth/me`,
        {
          headers: { Authorization: `Bearer ${response.access_token}` },
        },
      )

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user profile')
      }

      const user: User = await userResponse.json()

      set({
        user,
        token: response.access_token,
        isAuthenticated: true,
        isLoading: false,
      })
      return true
    } catch {
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return false
    }
  },

  refreshAccessToken: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      return false
    }

    try {
      const response = await authApi.refreshToken(refreshToken)
      // Rotate refresh token in localStorage
      localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
      // Update access token in memory only
      set({ token: response.access_token })
      return true
    } catch {
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      })
      return false
    }
  },
}))
